// Copyright (c) 2016 Deere & Company

import Claim, {getDefaultState as getClaimDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-battery';
import {
    addEmptyClaimRows,
    removeEmptyRows
} from '../../shared/services/empty-claim-rows';
import {
    findMatchingAccount,
    findMatchingKey,
    getPartUnit
} from '../../shared/services/claim-shared-helper';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimComplaintCause from '@deere/cps-it.warranty-model-factory/lib/claim-complaint-cause';
import actions from '../../action-list';
import {getDefaultState as getClaimPartDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-part';
import {getDefaultState} from '../state';
import {getDefaultState as getLaborDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-labor';
import {getDefaultState as getOtherCreditDefaultState} from '@deere/cps-it.warranty-model-factory/lib/claim-item-other-credit';

const copyClaim = state => {

    const defaultClaim = getClaimDefaultState();

    return Claim.update(state, {
        $merge: {
            allowedActions: [
                new ClaimAction({
                    actionName: 'DRAFT',
                    id: 'DRAFT',
                    mode: 'CHG'
                }),
                new ClaimAction({
                    actionName: 'SAVE_VALD',
                    id: 'SAVE_VALD',
                    mode: 'CHG'
                })
            ],
            claimNumber: defaultClaim.claimNumber,
            claimStatus: defaultClaim.claimStatus,
            claimValueDetails: defaultClaim.claimValueDetails,
            complaintCauseCommentsBasis: new ClaimComplaintCause({
                ...state.complaintCauseCommentsBasis,
                comments: defaultClaim.complaintCauseCommentsBasis.comments
            }),
            errors: defaultClaim.errors,
            recordInfo: defaultClaim.recordInfo,
            reimbursementDetails: defaultClaim.reimbursementDetails
        }
    });

};

const updatePartProperty = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const updatePartsUnit = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                unit: {
                    $set: action.data
                }
            }
        }
    });

const updateFailureDate = (state, action) =>
    Claim.update(state, {
        failureInfo: {
            failureDate: {
                $set: action.failureDate
            }
        }
    });

const additionalRepair = (state, action) => {

    const defaultClaim = getClaimDefaultState();

    return Claim.update(defaultClaim, {
        $merge: {
            failureInfo: {
                ...defaultClaim.failureInfo,
                failureDate: action.data.failureInfo.failureDate,
                repairDate: action.data.failureInfo.repairDate
            },
            pin: action.data.pin,
            referenceInfo: {
                ...defaultClaim.referenceInfo,
                ownershipStatus: action.data.referenceInfo.ownershipStatus,
                referenceNumber1: action.data.referenceInfo.referenceNumber1
            },
            responsibleAccount: action.data.responsibleAccount,
            useAmounts: action.data.useAmounts
        }
    });

};

const updateRepairDate = (state, action) =>
    Claim.update(state, {
        failureInfo: {
            repairDate: {
                $set: action.repairDate
            }
        }
    });

const updateFailurePart = (state, action) =>
    Claim.update(state, {
        failureInfo: {
            failurePartNumber: {
                $set: action.partNumber
            }
        }
    });

const updateWorkOrderTwo = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            referenceNumber2: {
                $set: action.workOrder
            }
        }
    });

const updateRepairNumber = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            repairNumber: {
                $set: action.repairNumber
            }
        }
    });

const updateWorkOrderOne = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            referenceNumber1: {
                $set: action.workOrder
            }
        }
    });

const updateClaimProperty = (state, action) =>
    Claim.update(state, {
        [action.property]: {
            $set: action.value
        }
    });

const updateResponsibleAccounts = (state, action) =>
    Claim.update(state, {
        responsibleAccount: {
            $set: findMatchingAccount(action.accountId, action.dealerCollection)
        }
    });

const updateInstalledBy = (state, action) =>
    Claim.update(state, {
        installedBy: {
            $set: findMatchingKey(action.key, action.installedByCollection)

        }
    });

const updateOwnershipStatus = (state, action) =>
    Claim.update(state, {
        referenceInfo: {
            ownershipStatus: {
                $set: findMatchingKey(action.key, action.statusCollection)
            }
        }
    });

const updateApplication = (state, action) =>
    Claim.update(state, {
        application: {
            $set: findMatchingKey(action.application, action.collection)
        }
    });

const updatePartDetails = (state, action) =>
    Claim.update(state, {
        parts: {
            [action.index]: {
                $merge: {
                    partDescription: action.partDetails.partDescription,
                    partNumber: action.partDetails.partNumber,
                    unit: getPartUnit(state.parts[action.index].unit, action.partDetails.unit[0])
                }
            }
        }
    });

const updateUseAmount = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            [action.index]: {
                amount: {
                    $set: action.amount
                }
            }
        }
    });

const updateUseAmounts = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            $set: action.useAmounts
        }
    });

const updateUseAmountOnProductLoad = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            $push: [action.useAmount]
        }
    });

const updateUnitDropDown = (state, action) =>
    Claim.update(state, {
        useAmounts: {
            0: {
                unit: {
                    $set: findMatchingKey(action.key, action.unitCollection)
                }
            }
        }
    });

const createLabor = state =>
    Claim.update(state, {
        labor: {
            $push: [getLaborDefaultState()]
        }
    });

const addPart = state =>
    Claim.update(state, {
        parts: {
            $push: [getClaimPartDefaultState()]
        }
    });

const deleteLabor = (state, action) =>
    Claim.update(state, {
        labor: {
            $splice: [[action.index, 1]]
        }
    });

const updateLaborProperty = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const updateLaborType = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                laborType: {
                    key: {
                        $set: action.laborType
                    }
                }
            }
        }
    });

const updateLaborLocation = (state, action) =>
    Claim.update(state, {
        labor: {
            [action.index]: {
                location: {
                    key: {
                        $set: action.location
                    }
                }
            }
        }
    });

const deletePart = (state, action) =>
    Claim.update(state, {
        parts: {
            $splice: [[action.index, 1]]
        }
    });

const resetUseAmounts = state =>
    Claim.update(state, {
        useAmounts: {
            $set: getDefaultState().claim.useAmounts
        }
    });

const updateCredits = (state, action) =>
    Claim.update(state, {
        otherCredits: {
            [action.index]: {
                [action.property]: {
                    $set: action.data
                }
            }
        }
    });

const createOtherCredit = state =>
    Claim.update(state, {
        otherCredits: {
            $push: [getOtherCreditDefaultState()]
        }
    });

const deleteOtherCredit = (state, action) =>
    Claim.update(state, {
        otherCredits: {
            $splice: [[action.index, 1]]
        }
    });

const updateFailureInfoDropDown = (state, action) =>
    Claim.update(state, {
        additionalFailureInfo: {
            [action.property]: {
                $set: findMatchingKey(action.key, action.collection)
            }
        }
    });

const updateFailureInfo = (state, action) =>
    Claim.update(state, {
        additionalFailureInfo: {
            [action.property]: {
                $set: action.value
            }
        }
    });

const resetBatteryClaim = () =>
    getClaimDefaultState();

const updateComplaintCauseCorrection = (state, action) =>
    Claim.update(state, {
        complaintCauseCommentsBasis: {
            [action.property]: {
                $set: action.value
            }
        }
    });

const addFile = (state, action) =>
    Claim.update(state, {
        file: {
            $push: [action.data]
        }
    });

const updateFile = (state, action) =>
    Claim.update(state, {
        file: {
            $set: action.files
        }
    });

const deleteFile = (state, action) => {

    const index = state.file.findIndex(file => file.serialNumber === action.data.serialNumber && file.name === action.data.name);

    return Claim.update(state, {
        file: {
            $splice: [[index, 1]]
        }
    });

};

const updateClaimMode = (state, action) =>
    Claim.update(state, {
        $set: action.isEditable ?
            addEmptyClaimRows(state, Claim) :
            removeEmptyRows(state, Claim)
    });

const setBatteryClaim = (state, action) =>
    Claim.update(state, {
        $set: action.isEditable ?
            addEmptyClaimRows(action.updatedClaim, Claim) :
            action.updatedClaim
    });

const updateGoodsInstallationVat = (state, action) =>
    Claim.update(state, {
        goodsInstallationVat: {
            $set: action.goodsInstallationVat
        }
    });

const reviseBatteryClaim = state =>
    Claim.update(state, {
        errors: {
            $set: []
        }
    });

export default (state = getDefaultState().claim, action) => {

    const actionTypes = {
        [actions.ADD_CLAIM_BATTERY_ADDITIONAL_REPAIR]: additionalRepair,
        [actions.ADD_CLAIM_BATTERY_FILE]: addFile,
        [actions.DELETE_CLAIM_BATTERY_FILE]: deleteFile,
        [actions.COPY_CLAIM_BATTERY]: copyClaim,
        [actions.CREATE_CLAIM_BATTERY_OTHER_CREDIT]: createOtherCredit,
        [actions.DELETE_CLAIM_BATTERY_OTHER_CREDIT]: deleteOtherCredit,
        [actions.UPDATE_CLAIM_BATTERY_OTHER_CREDIT_DESCRIPTION]: updateCredits,
        [actions.UPDATE_CLAIM_BATTERY_OTHER_CREDIT_AMOUNT]: updateCredits,
        [actions.UPDATE_CLAIM_BATTERY_OTHER_CREDIT_DISTANCE]: updateCredits,
        [actions.UPDATE_CLAIM_BATTERY_OTHER_CREDIT_COMMENTS]: updateCredits,
        [actions.CREATE_CLAIM_BATTERY_LABOR]: createLabor,
        [actions.UPDATE_CLAIM_BATTERY_LABOR_FIELDS]: updateLaborProperty,
        [actions.UPDATE_CLAIM_BATTERY_LABOR_LOCATION]: updateLaborLocation,
        [actions.UPDATE_CLAIM_BATTERY_LABOR_TYPE]: updateLaborType,
        [actions.DELETE_CLAIM_BATTERY_LABOR]: deleteLabor,
        [actions.CREATE_CLAIM_BATTERY_PART]: addPart,
        [actions.DELETE_CLAIM_BATTERY_PART]: deletePart,
        [actions.RESET_CLAIM_BATTERY]: resetBatteryClaim,
        [actions.RESET_CLAIM_BATTERY_USE_AMOUNTS]: resetUseAmounts,
        [actions.REVISE_CLAIM_BATTERY]: reviseBatteryClaim,
        [actions.SET_CLAIM_BATTERY]: setBatteryClaim,
        [actions.SET_CLAIM_BATTERY_PART_DETAILS]: updatePartDetails,
        [actions.UPDATE_CLAIM_BATTERY_APPLICATION]: updateApplication,
        [actions.UPDATE_CLAIM_BATTERY_BASIS_FOR_SETTLEMENT]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_BATTERY_COMPLAINT_CAUSE]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_BATTERY_COMPLAINT_CORRECTION]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_BATTERY_DTAC_CASE]: updateFailureInfo,
        [actions.UPDATE_CLAIM_BATTERY_DTAC_SOLUTION]: updateFailureInfo,
        [actions.UPDATE_CLAIM_BATTERY_INSTALLED_BY]: updateInstalledBy,
        [actions.UPDATE_CLAIM_BATTERY_IS_EDITABLE]: updateClaimMode,
        [actions.UPDATE_CLAIM_BATTERY_OPERATORS_COMPLAINT]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_BATTERY_OPERATORS_COMMENTS]: updateComplaintCauseCorrection,
        [actions.UPDATE_CLAIM_BATTERY_OWNERSHIP_STATUS]: updateOwnershipStatus,
        [actions.UPDATE_CLAIM_BATTERY_FAILURE_DATE]: updateFailureDate,
        [actions.UPDATE_CLAIM_BATTERY_FAILED_MODE]: updateFailureInfoDropDown,
        [actions.UPDATE_CLAIM_BATTERY_FAILED_MODE_COMMENTS]: updateFailureInfo,
        [actions.UPDATE_CLAIM_BATTERY_FAILURE_PART]: updateFailurePart,
        [actions.UPDATE_CLAIM_BATTERY_PART_FIELDS]: updatePartProperty,
        [actions.UPDATE_CLAIM_BATTERY_PART_SERIAL_NUMBER]: updateFailureInfo,
        [actions.UPDATE_CLAIM_BATTERY_PART_UNIT]: updatePartsUnit,
        [actions.UPDATE_CLAIM_BATTERY_PROPERTY]: updateClaimProperty,
        [actions.UPDATE_CLAIM_BATTERY_REPAIR_DATE]: updateRepairDate,
        [actions.UPDATE_CLAIM_BATTERY_REPAIR_NUMBER]: updateRepairNumber,
        [actions.UPDATE_CLAIM_BATTERY_RESPONSIBLE_ACCOUNTS]: updateResponsibleAccounts,
        [actions.UPDATE_CLAIM_BATTERY_USE_AMOUNT]: updateUseAmount,
        [actions.UPDATE_CLAIM_BATTERY_USE_AMOUNTS]: updateUseAmounts,
        [actions.UPDATE_CLAIM_BATTERY_USE_AMOUNT_ON_PRODUCT_LOAD]: updateUseAmountOnProductLoad,
        [actions.UPDATE_CLAIM_BATTERY_USE_AMOUNT_UNIT]: updateUnitDropDown,
        [actions.UPDATE_CLAIM_BATTERY_WORK_ORDER_ONE]: updateWorkOrderOne,
        [actions.UPDATE_CLAIM_BATTERY_WORK_ORDER_TWO]: updateWorkOrderTwo,
        [actions.UPDATE_CLAIM_BATTERY_GOODS_INSTALLATION_VAT]: updateGoodsInstallationVat,
        [actions.UPDATE_CLAIM_BATTERY_FILE_LIST]: updateFile
    };

    const reducer = actionTypes[action.type];

    return reducer ?
        reducer(state, action) :
        state;

};



// WEBPACK FOOTER //
// ./src/claim/battery/reducers/battery-claim.js